<template>
  <base-modal
    ref="modal"
    :title="title"
    :loading="isSaving"
    @show="toggleModal"
    @hidden="resetModal"
    @ok="handleOk"
    ok-title="Save"
    cancel-title="Cancel"
    cancel-variant="light"
    footer-class="modal-footer-custom"
    header-class="modal-header-custom"
    body-class="modal-body-custom"
    content-class="modal-content-custom"
  >
    <form ref="form" @submit.stop.prevent="handleSubmit">
      <div class="mb-6 pb-6 border-b border-border">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-muted mb-4">Content</div>

        <div class="mb-4"><label for="title" class="block text-[13px] font-medium text-ink mb-1.5">Title</label>
          <textarea
            id="title"
            v-model="requirement.title"
            required
            placeholder="Requirement title"
            rows="1"
            max-rows="10"
            class="w-full border border-border-hover rounded-[10px] px-3.5 py-2.5 text-sm font-[inherit] leading-normal transition-[border-color] duration-150 focus:border-primary focus:ring-3 focus:ring-primary/10 outline-none"
          ></textarea>
        </div>

        <div class="mb-4"><label for="subtitle" class="block text-[13px] font-medium text-ink mb-1.5">Subtitle</label>
          <input
            id="subtitle"
            v-model="requirement.subtitle"
            placeholder="Optional subtitle"
            class="w-full border border-border-hover rounded-[10px] px-3.5 py-2.5 text-sm font-[inherit] leading-normal transition-[border-color] duration-150 focus:border-primary focus:ring-3 focus:ring-primary/10 outline-none"
          >
        </div>

        <div class="mb-4"><label for="note" class="block text-[13px] font-medium text-ink mb-1.5">Note</label>
          <textarea
            id="note"
            v-model="requirement.note"
            placeholder="Internal notes..."
            rows="2"
            max-rows="10"
            class="w-full border border-border-hover rounded-[10px] px-3.5 py-2.5 text-sm font-[inherit] leading-normal transition-[border-color] duration-150 focus:border-primary focus:ring-3 focus:ring-primary/10 outline-none"
          ></textarea>
        </div>
      </div>

      <div class="mb-6 pb-6 border-b border-border">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-muted mb-4">Tasks</div>

        <div class="flex gap-2 mb-3">
          <input
            v-model="taskForm.title"
            placeholder="Add a task..."
            class="w-full border border-border-hover rounded-[10px] px-3.5 py-2.5 text-sm font-[inherit] leading-normal transition-[border-color] duration-150 focus:border-primary focus:ring-3 focus:ring-primary/10 outline-none"
            @keydown.enter.prevent="addTask"
          >
          <button type="button" class="shrink-0 bg-[#f4f5fa] border-0 rounded-[10px] px-4 text-[13px] font-medium text-ink cursor-pointer transition-[background] duration-150 hover:bg-[#e2e4ed]" @click="addTask">Add</button>
        </div>

        <div v-if="requirement.tasks && requirement.tasks.length > 0" class="flex flex-col gap-0.5">
          <div class="group flex items-center gap-1 py-1.5 px-2 rounded-lg transition-[background] duration-150 hover:bg-canvas" v-for="(task, index) in requirement.tasks" :key="index">
            <input type="checkbox" v-model="task.isComplete">
            <span class="flex-1 text-sm text-ink" :class="{ 'line-through !text-muted': task.isComplete }">{{ task.title }}</span>
            <button type="button" class="bg-transparent border-0 p-0.5 text-muted cursor-pointer opacity-40 transition-[opacity,color] duration-150 flex items-center group-hover:opacity-100 hover:!text-[#e53935]" @click="deleteTask(task)">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>
        </div>
      </div>

      <div class="mb-6 pb-6 border-b border-border">
        <div class="text-[11px] font-semibold uppercase tracking-wide text-muted mb-4">Schedule & assignment</div>

        <div class="flex gap-4">
          <div class="mb-4 flex-1"><label for="date" class="block text-[13px] font-medium text-ink mb-1.5">Deadline</label>
            <date-input
              v-model="requirement.deadline"
              placeholder="Select date"
            ></date-input>
          </div>

          <div class="mb-4 flex-1"><label for="status" class="block text-[13px] font-medium text-ink mb-1.5">Status</label>
            <base-select v-model="requirement.status" :options="applicationStatuses" value-key="value" label-key="text" placeholder="Select status"></base-select>
          </div>
        </div>

        <div class="flex gap-4">
          <div class="mb-4 flex-1"><label for="category" class="block text-[13px] font-medium text-ink mb-1.5">Category</label>
            <base-select v-model="requirement.category" :options="requirementTypes" value-key="value" label-key="text" placeholder="Select category"></base-select>
          </div>

          <div class="mb-4 flex-1"><label class="block text-[13px] font-medium text-ink mb-1.5">Assigned to</label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="opt in managersFiltered"
                :key="opt.value"
                type="button"
                class="flex items-center gap-2 px-2.5 py-1.5 rounded-full text-[13px] font-medium border cursor-pointer transition-all duration-150"
                :class="isAssigned(opt.value)
                  ? 'bg-[#f0eef5] border-primary/30 text-primary'
                  : 'bg-white border-border-hover text-muted hover:border-primary/20 hover:text-ink'"
                @click="toggleAssigned(opt.value)">
                <img v-if="getPersonPhoto(opt.value)" :src="getPersonPhoto(opt.value)" :alt="opt.text" class="w-5 h-5 rounded-full object-cover">
                <div v-else class="w-5 h-5 rounded-full bg-[#ecedf3] text-primary flex items-center justify-center font-bold text-[9px]">{{ opt.text.charAt(0) }}</div>
                <span>{{ opt.text }}</span>
                <svg v-if="isAssigned(opt.value)" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="text-primary"><polyline points="20 6 9 17 4 12"></polyline></svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div class="text-[11px] font-semibold uppercase tracking-wide text-muted mb-4">Documents</div>

        <div class="mb-4"><label for="documentUrl" class="block text-[13px] font-medium text-ink mb-1.5">Document URL</label>
          <input
            id="documentUrl"
            v-model="requirement.documentUrl"
            placeholder="https://..."
            class="w-full border border-border-hover rounded-[10px] px-3.5 py-2.5 text-sm font-[inherit] leading-normal transition-[border-color] duration-150 focus:border-primary focus:ring-3 focus:ring-primary/10 outline-none"
          >
        </div>

        <div class="mb-4"><label for="publicDocumentUrl" class="block text-[13px] font-medium text-ink mb-1.5">Public document URL</label>
          <input
            id="publicDocumentUrl"
            v-model="requirement.publicDocumentUrl"
            placeholder="https://..."
            class="w-full border border-border-hover rounded-[10px] px-3.5 py-2.5 text-sm font-[inherit] leading-normal transition-[border-color] duration-150 focus:border-primary focus:ring-3 focus:ring-primary/10 outline-none"
          >
        </div>
      </div>
    </form>
  </base-modal>
</template>

<script>
import { mapState } from 'vuex'
import dayjs from 'dayjs'
import DateInput from '../../UIComponents/DateInput'
import BaseSelect from '../../UIComponents/BaseSelect'
import { PHOTOS } from '@/services/photos'

export default {
  components: { DateInput, BaseSelect },
  props: ['item', 'applicant', 'requirementIndex'],
  data () {
    return {
      requirement: {
        title: '',
        subtitle: '',
        createdAt: '',
        type: 'promise',
        category: 'mandatoryCriteria',
        deadline: '',
        assignedTo: [],
        note: '',
        status: 'created',
        documentUrl: '',
        tasks: [],
        publicDocumentUrl: ''
      },
      isSaving: false,
      taskForm: {
        title: '',
        isComplete: false
      },
      applicantId: null,
      requirements: [],
      tasks: []
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      statusOptions: state => state.auth.statusOptions,
      applicationTypes: state => state.auth.applicationTypes,
      requirementTypes: state => state.auth.requirementTypes,
      managers: state => state.managers.managers,
      applicationStatuses: state => state.components.applicationStatuses
    }),
    title () {
      if (this.item) {
        return 'Edit requirement'
      } else {
        return 'Add requirement'
      }
    },
    managersFiltered () {
      const visible = this.managers.filter(m => !m.hidden)
      if (!this.applicant || !this.applicant.availableTo || this.applicant.availableTo.length === 0) {
        return visible
      } else {
        return visible.filter(manager => this.applicant.availableTo.includes(manager.value))
      }
    }
  },
  methods: {
    isAssigned (value) {
      return this.requirement.assignedTo && this.requirement.assignedTo.includes(value)
    },
    toggleAssigned (value) {
      if (!this.requirement.assignedTo) {
        this.requirement.assignedTo = []
      }
      const idx = this.requirement.assignedTo.indexOf(value)
      if (idx === -1) {
        this.requirement.assignedTo.push(value)
      } else {
        this.requirement.assignedTo.splice(idx, 1)
      }
    },
    getPersonPhoto (name) {
      return PHOTOS[name] || null
    },
    addTask () {
      if (!this.taskForm.title.trim()) return
      this.requirement.tasks.push({
        title: this.taskForm.title,
        isComplete: this.taskForm.isComplete
      })
      this.taskForm.title = ''
      this.taskForm.isComplete = false
    },
    deleteTask (task) {
      this.requirement.tasks.splice(this.requirement.tasks.indexOf(task), 1)
    },
    toggleTask (task) {
      task.isComplete = !task.isComplete
    },
    checkFormValidity () {
      const valid = this.$refs.form.checkValidity()
      this.nameState = valid ? 'valid' : 'invalid'
      return valid
    },
    handleSubmit () {
      this.isSaving = true
      this.requirements[this.requirementIndex] = this.requirement
      const { requirement, applicantId, requirements } = this
      const userName = this.user ? (this.user.firstName || this.user.username) : 'Unknown'
      if (this.item) {
        // Track before/after changes
        const changes = []
        if (this.item.status !== requirement.status) {
          changes.push({ field: 'Status', from: this.item.status || 'none', to: requirement.status })
        }
        const oldAssigned = (this.item.assignedTo || []).join(', ')
        const newAssigned = (requirement.assignedTo || []).join(', ')
        if (oldAssigned !== newAssigned) {
          changes.push({ field: 'Assigned to', from: oldAssigned || 'none', to: newAssigned || 'none' })
        }
        if ((this.item.category || '') !== (requirement.category || '')) {
          changes.push({ field: 'Category', from: this.item.category || 'none', to: requirement.category || 'none' })
        }
        if ((this.item.deadline || '') !== (requirement.deadline || '')) {
          changes.push({ field: 'Deadline', from: this.item.deadline || 'none', to: requirement.deadline || 'none' })
        }

        this.$store.dispatch('UPDATE_APPLICANT_REQUIREMENTS', { applicantId, requirements }).then(() => {
          this.isSaving = false
          this.$emit('update')
          this.$store.dispatch('UPDATE_APPLICANT_PUBLIC_REQUIREMENTS', { applicantId, requirements })
          const activityPayload = {
            applicantId: applicantId,
            type: changes.some(c => c.field === 'Status') ? 'status' : 'edit',
            message: changes.length ? 'updated requirement "' + requirement.title + '"' : 'updated requirement "' + requirement.title + '"',
            user: userName
          }
          if (changes.length) {
            activityPayload.changes = changes
          }
          this.$store.dispatch('CREATE_ACTIVITY', activityPayload).catch(() => {})
          this.$store.dispatch('SHOW_TOAST', { message: 'Requirement updated' })
          this.$nextTick(() => {
            this.$refs.modal.hide()
          })
        }).catch(() => { this.isSaving = false })
      } else {
        this.requirement.createdAt = dayjs().format()
        this.$store.dispatch('ADD_APPLICANT_REQUIREMENT', { applicantId, requirement }).then(() => {
          this.isSaving = false
          this.$emit('update')
          this.$store.dispatch('CREATE_ACTIVITY', {
            applicantId: applicantId,
            type: 'requirement',
            message: 'added requirement "' + requirement.title + '"',
            user: userName
          }).catch(() => {})
          this.$store.dispatch('SHOW_TOAST', { message: 'Requirement added' })
          this.$nextTick(() => {
            this.$refs.modal.hide()
          })
        }).catch(() => { this.isSaving = false })
      }
    },
    resetModal () {
      this.requirement.title = ''
      this.requirement.createdAt = ''
      this.requirement.type = this.applicant.type
      this.requirement.category = 'mandatoryCriteria'
      this.requirement.deadline = ''
      this.requirement.assignedTo = []
      this.requirement.note = ''
      this.requirement.status = 'created'
      this.requirement.documentUrl = ''
      this.requirement.publicDocumentUrl = ''
      this.requirement.subtitle = ''
      this.requirement.tasks = []
      this.requirements = []
    },
    showModal () {
      this.$refs['modal'].show()
    },
    hideModal () {
      this.$refs['modal'].hide()
    },
    toggleModal () {
      this.resetModal()
      if (this.item) {
        this.requirement.title = this.item.title
        this.requirement.createdAt = this.item.createdAt
        this.requirement.type = this.item.type ? this.item.type : this.applicant.type
        this.requirement.category = this.item.category ? this.item.category : ''
        this.requirement.deadline = this.item.deadline ? this.item.deadline : ''
        this.requirement.assignedTo = this.item.assignedTo ? this.item.assignedTo : []
        this.requirement.note = this.item.note ? this.item.note : ''
        this.requirement.status = this.item.status ? this.item.status : 'created'
        this.requirement.documentUrl = this.item.documentUrl ? this.item.documentUrl : ''
        this.requirement.publicDocumentUrl = this.item.publicDocumentUrl ? this.item.publicDocumentUrl : ''
        this.requirement.subtitle = this.item.subtitle ? this.item.subtitle : ''
        this.requirement.tasks = this.item.tasks ? this.item.tasks : []
        this.requirements = this.applicant.requirements
      }
      this.applicantId = this.applicant.id
    },
    handleOk (bvModalEvt) {
      bvModalEvt.preventDefault()
      this.handleSubmit()
    }
  },
  mounted () {
  },
  created () {
  }
}
</script>
